import type { HTMLAttributes, ReactNode } from "react";
import * as styles from "./EmptyState.style.ts";
import { H4 } from "src/components/Heading/Heading.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import Warning from "src/assets/warning.svg?react";

export type EmptyStateProps = {
  image?: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  description?: ReactNode;
  /**
   * Picks a built-in image. Omit it for a state that is merely empty rather
   * than wrong: the warning triangle reads as a failure, and three panels of
   * the recommendation module used it for "this customer has no
   * recommendations" and the like — normal answers, dressed as alarms.
   */
  type?: "warning";
} & HTMLAttributes<HTMLDivElement>;

export function EmptyState({
  image: imageProp,
  title,
  action,
  description,
  type,
  ...rest
}: EmptyStateProps) {
  const imageMap = {
    warning: <Warning />,
  };
  // An explicit image wins over the built-in one. The order used to be the
  // other way round, which made `image` unreachable: `type` was required, so
  // the map always answered first.
  const image = imageProp ?? (type ? imageMap[type] : undefined);
  return (
    <div css={styles.root} {...rest}>
      {image && <div>{image}</div>}
      {(title || description) && (
        <div css={styles.content}>
          {title && <H4>{title}</H4>}
          {description && <TextV2 variant={"body1"}>{description}</TextV2>}
        </div>
      )}

      {action && <div>{action}</div>}
    </div>
  );
}
